//codex-- Share the updated toolbar presentation only among pages in this folder.
import React, { useState } from 'react';
import { ArrowPathIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import './PageToolbar.css';

//codex-- Keep actions above filters while preserving each page's handlers and state.
export default function PageToolbar({ title, actions, children, className = '', ...sectionProps }) {
  return (
    <section {...sectionProps} aria-label={title} className={`page-toolbar ${className}`}>
      <div className="page-toolbar-heading">
        <p>{title}</p>
        {actions && <div className="page-toolbar-actions" aria-label={`${title} actions`}>{actions}</div>}
      </div>
      {children && <div className="page-toolbar-body">{children}</div>}
    </section>
  );
}

//codex-- Associate visible labels with their controls without changing existing IDs or callbacks.
export function ToolbarField({ label, children }) {
  return <label className="page-toolbar-field"><span>{label}</span>{children}</label>;
}

//codex-- Replace overlapping search buttons with a labeled input and separate responsive actions.
export function PageToolbarSearch({ search, handleChange, onClick, onReset, loading = false,
  label = 'Search', placeholder = 'Enter a search term', enterArgument = true }) {
  return (
    <div className="page-toolbar-search">
      <ToolbarField label={label}>
        <div className="page-toolbar-search-input">
          <MagnifyingGlassIcon aria-hidden="true" />
          <input type="text" name="search" value={search ?? ''} onChange={handleChange}
            placeholder={placeholder} onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (enterArgument) onClick?.(handleChange);
                else onClick?.();
              }
            }} />
        </div>
      </ToolbarField>
      <div className="page-toolbar-search-actions">
        <button type="button" onClick={onClick} disabled={!search || loading} className="bg-green-600">
          {loading ? 'Searching…' : 'Search'}
        </button>
        <button type="button" onClick={onReset || (() => window.location.reload())} className="bg-orange-600">
          <ArrowPathIcon aria-hidden="true" className="h-4 w-4" />Reset
        </button>
      </div>
    </div>
  );
}

//codex-- Keep the holds search callback and reset behavior while using the page toolbar layout.
export function ToolbarHoldSearch({ value, onChange, onSearch, onReset, loading, placeholder }) {
  return <PageToolbarSearch search={value} handleChange={onChange} onClick={onSearch} onReset={onReset}
    loading={loading} placeholder={placeholder} label="Search holds" enterArgument={false} />;
}

//codex-- Preserve WIP search scope, typing callbacks, and the five-entry local search history.
export function ToolbarDropdownSearch({ filterOptions = ['All', 'Active', 'Job Site', 'PM'],
  defaultFilter = 'All', placeholder = 'Enter a search term', onSearch, onTyping, onReset }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState(defaultFilter);
  const [history, setHistory] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('searchHistory') || '[]');
      return Array.isArray(saved) ? saved.filter((term) => typeof term === 'string') : [];
    }
    catch { return []; }
  });
  const search = () => {
    onSearch?.(searchTerm, filterType);
    if (!searchTerm.trim()) return;
    const updated = [searchTerm, ...history.filter((term) => term !== searchTerm)].slice(0, 5);
    setHistory(updated);
    localStorage.setItem('searchHistory', JSON.stringify(updated));
  };
  const reset = () => {
    setSearchTerm('');
    setFilterType(defaultFilter);
    setHistory([]);
    onReset?.();
    localStorage.removeItem('searchHistory');
  };
  return (
    <div className="page-toolbar-dropdown-search">
      <div className="page-toolbar-row">
        <ToolbarField label="Search WIP projects">
          <input type="text" value={searchTerm} placeholder={placeholder}
            onChange={(event) => { setSearchTerm(event.target.value); onTyping?.(event.target.value, filterType); }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') { event.preventDefault(); search(); }
              if (event.key === 'Escape') { event.preventDefault(); reset(); }
            }} />
        </ToolbarField>
        <ToolbarField label="Search in">
          <select value={filterType} onChange={(event) => {
            setFilterType(event.target.value);
            onTyping?.(searchTerm, event.target.value);
          }}>{filterOptions.map((option) => <option key={option}>{option}</option>)}</select>
        </ToolbarField>
        <button type="button" onClick={search} className="bg-green-600">Search</button>
        <button type="button" onClick={reset} className="bg-orange-600">Reset</button>
      </div>
      {history.length > 0 && <div className="page-toolbar-history" aria-label="Recent searches">
        <span>Recent</span>
        {history.map((term) => <button type="button" key={term} onClick={() => {
          setSearchTerm(term); onSearch?.(term, filterType);
        }}>{term}</button>)}
      </div>}
    </div>
  );
}
